import {
  Component,
  output,
  signal,
  computed,
  ChangeDetectionStrategy,
  OnDestroy,
  OnInit,
  viewChildren,
  ElementRef,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, FormArray, AbstractControl } from '@angular/forms';
import { Subject, timer } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

interface AccountOption {
  id: string;
  name: string;
  number: string;
  available: string;
  type: string;
}

@Component({
  selector: 'app-own-account-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './own-account-modal.html',
  styleUrls: ['./own-account-modal.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OwnAccountModal implements OnInit, OnDestroy {
  close = output<void>();
  pinInputs = viewChildren<ElementRef<HTMLInputElement>>('pinInput');

  private readonly fb = inject(FormBuilder);
  private readonly destroy$ = new Subject<void>();

  readonly step = signal<number>(1);
  readonly isProcessing = signal<boolean>(false);
  readonly showCancelConfirm = signal<boolean>(false);
  readonly apiError = signal<string | null>(null);

  // Dropdown signals
  readonly isFromDropdownOpen = signal<boolean>(false);
  readonly isToDropdownOpen = signal<boolean>(false);

  readonly accounts: AccountOption[] = [
    { id: '1', name: 'Credit Card 1', number: '**** 1234', available: '₹6,280,050.75', type: 'Credit Card' },
    { id: '2', name: 'HDFC Bank', number: '**** 5678', available: '₹1,45,000.00', type: 'Bank Account' },
    { id: '3', name: 'SBI Bank', number: '**** 9012', available: '₹50,000.00', type: 'Bank Account' }
  ];

  readonly selectedSource = signal<AccountOption>(this.accounts[0]);
  readonly selectedDestination = signal<AccountOption>(this.accounts[1]);

  readonly finalAmount = signal<string>('0.00');
  readonly fee = signal<string>('25.00');

  // Computed
  readonly formattedAvailable = computed(() => this.selectedSource().available);

  // Validation Signals
  readonly canProceedStep1Signal = signal<boolean>(false);
  // Step 3 is PIN, step 2 is review

  form!: FormGroup;

  constructor() {
    this.initForm();
  }

  ngOnInit(): void {
    // Amount Value Changes
    this.form.get('amount')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(val => {
      this.checkStep1Validity();
      if (val) {
        const numericVal = parseFloat(val);
        if (!isNaN(numericVal)) {
          const formatted = numericVal.toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          });
          this.finalAmount.set(formatted);
        }
      } else {
        this.finalAmount.set('0.00');
      }
    });

    this.form.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.form.get('pin')?.dirty) {
        this.apiError.set(null);
      }
    });

    this.checkStep1Validity();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initForm(): void {
    this.form = this.fb.group({
      amount: ['', [Validators.required, Validators.pattern(/^\d+(\.\d{1,2})?$/), Validators.min(1)]],
      pin: this.fb.array(Array(6).fill('').map(() => this.fb.control('', [
        Validators.required,
        Validators.pattern(/^\d$/)
      ])))
    });
  }

  get pinControls(): AbstractControl[] {
    return (this.form.get('pin') as FormArray).controls;
  }

  // --- Validation Helpers ---
  private checkStep1Validity(): void {
    if (!this.form) {
      this.canProceedStep1Signal.set(false);
      return;
    }
    const amountControl = this.form.get('amount');
    const isValid = amountControl?.valid && parseFloat(amountControl?.value || '0') > 0;
    this.canProceedStep1Signal.set(!!isValid);
  }

  isFieldInvalid(fieldName: string): boolean {
    const control = this.form.get(fieldName);
    return !!(control && control.invalid && (control.touched || control.dirty));
  }

  // --- Dropdown Actions ---

  toggleFromDropdown(event: Event) {
    event.stopPropagation();
    this.isFromDropdownOpen.update(v => !v);
    this.isToDropdownOpen.set(false);
  }

  toggleToDropdown(event: Event) {
    event.stopPropagation();
    this.isToDropdownOpen.update(v => !v);
    this.isFromDropdownOpen.set(false);
  }

  selectSource(account: AccountOption, event: Event) {
    event.stopPropagation();
    this.selectedSource.set(account);
    this.isFromDropdownOpen.set(false);

    // Prevent duplicate source/dest
    if (this.selectedDestination().id === account.id) {
      const other = this.accounts.find(a => a.id !== account.id);
      if (other) this.selectedDestination.set(other);
    }
  }

  selectDestination(account: AccountOption, event: Event) {
    event.stopPropagation();
    this.selectedDestination.set(account);
    this.isToDropdownOpen.set(false);

    if (this.selectedSource().id === account.id) {
      const other = this.accounts.find(a => a.id !== account.id);
      if (other) this.selectedSource.set(other);
    }
  }

  closeDropdowns() {
    this.isFromDropdownOpen.set(false);
    this.isToDropdownOpen.set(false);
  }

  swapAccounts() {
    const from = this.selectedSource();
    const to = this.selectedDestination();
    this.selectedSource.set(to);
    this.selectedDestination.set(from);
  }

  setMaxAmount() {
    const max = 10000; // Mock logic
    this.form.patchValue({ amount: max });
    this.form.get('amount')?.markAsDirty();
    this.form.get('amount')?.markAsTouched();
    this.checkStep1Validity();
  }

  // --- Step Navigation ---

  nextStep() {
    const currentStep = this.step();

    if (currentStep === 1) {
      const amount = this.form.get('amount');
      amount?.markAsTouched();
      amount?.updateValueAndValidity();
      this.checkStep1Validity();

      if (this.form.get('amount')?.invalid) return;
    }

    if (currentStep === 3) {
      // Pin validation
      const pinArray = this.form.get('pin') as FormArray;
      this.apiError.set(null);
      pinArray.markAsTouched();
      pinArray.controls.forEach(c => c.markAsTouched());

      if (pinArray.invalid) return;

      this.processTransaction();
      return;
    }

    if (currentStep < 4) {
      this.step.update(s => s + 1);
    } else {
      this.confirmCancel();
    }
  }

  prevStep() {
    if (this.step() > 1) {
      this.step.update(s => s - 1);
    }
  }

  processTransaction() {
    // Simulate PIN verification
    const pinValue = (this.form.get('pin') as FormArray).value.join('');

    this.step.set(4);
    this.isProcessing.set(true);

    timer(1500)
      .pipe(
        finalize(() => {
          this.isProcessing.set(false);
        })
      )
      .subscribe(() => {
        // Mock validation: fail if PIN is '000000'
        if (pinValue === '000000') {
          this.step.set(3); // Go back to PIN step
          this.apiError.set('Incorrect PIN. Please try again.');
          (this.form.get('pin') as FormArray).reset();
          this.pinInputs()[0]?.nativeElement.focus();
        }
      });
  }

  onClose() {
    const s = this.step();
    if (s === 1 || s === 4) {
      this.confirmCancel();
    } else {
      this.showCancelConfirm.set(true);
    }
  }

  confirmCancel() {
    this.close.emit();
  }

  closeCancelConfirm() {
    this.showCancelConfirm.set(false);
  }

  // --- PIN Logic ---

  onPinInput(index: number, event: Event) {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    if (value && index < 5) {
      const inputs = this.pinInputs();
      if (inputs[index + 1]) {
        inputs[index + 1].nativeElement.focus();
      }
    }
  }

  onPinBackspace(index: number, event: Event) {
    const keyboardEvent = event as KeyboardEvent;
    const input = keyboardEvent.target as HTMLInputElement;
    const value = input.value;
    if (!value && index > 0) {
      const inputs = this.pinInputs();
      if (inputs[index - 1]) {
        inputs[index - 1].nativeElement.focus();
      }
    }
  }

  trackByFn(index: number): number {
    return index;
  }
}